import { Pagination, PaginationQuery } from "@/models/base/Pagination";
import { Legislation } from "../models/legislation/Legislation";
import { useItemService } from "./useServices";
import { getDbValue, LegislationSourceKey } from "@/config/datasources";
import { useQuery } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { useEffect, useRef } from "react";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { TimelineItem } from "@/models/legislation/Timeline";
import { applyConsolidationDevMetadata } from "@/lib/consolidationDevFixtures";
import { normalizeTimelineDates } from "@/lib/timelineDates";
import { requestFailedError, shouldRetryRequest } from "@/lib/queryRetry";
import { restError } from "@/models/client/RestResponse";

interface UseGetPaginatedOptions {
  onPageMismatch?: (requestedPage: number, actualPage: number) => void;
}

const useLegislation = () => {
  /**** Services ****/
  const legislationService = useItemService((services) => services.legislation);

  /**** Queries & Mutations ****/
  const useGetPaginated = (
    params: PaginationQuery,
    sources?: LegislationSourceKey[],
    options?: UseGetPaginatedOptions
  ) => {
    const errorHandledRef = useRef(false);
    const pageMismatchHandledRef = useRef(false);
    const { onPageMismatch } = options || {};
    const acceptableFailureCount = 5;

    const query = useQuery<Pagination<Legislation>>({
      queryKey: [PATH_KEYS.legislation, "pagination", params, sources],
      // The signal is react-query's own: the legislation list is driven by a
      // type-ahead search box, so a superseded request must be aborted rather than
      // left running its full query against the database (LAR-445).
      queryFn: async ({ signal }) => {
        const fetchedPageContent = await legislationService.getPaginated(
          params,
          sources,
          undefined, // sourceFilters - not used in this hook
          signal
        );
        if (fetchedPageContent.success) {
          const value = fetchedPageContent.value;
          const normalizedData = value.data.map(applyConsolidationDevMetadata);
          // API returns totalRecords; normalize to totalCount for consumers
          return {
            ...value,
            data: normalizedData,
            totalCount: value.totalCount ?? value.totalRecords ?? 0,
          };
        } else throw requestFailedError(fetchedPageContent);
      },
      retry: (failureCount, error) =>
        shouldRetryRequest(failureCount, error, acceptableFailureCount),
      // Keep previous data when query fails
      placeholderData: (previousData) => previousData,
    });

    // Reset page mismatch flag when query succeeds or page number changes
    useEffect(() => {
      if (query.isSuccess) {
        pageMismatchHandledRef.current = false;
      }
    }, [query.isSuccess, params.pageNumber]);

    // A failed page request that lands us back on a different page is reported by the
    // page-mismatch effect below, which says which page the user ended up on; without
    // this guard a single failure stacks that toast on top of the generic one.
    const reportedAsPageMismatch =
      onPageMismatch !== undefined &&
      query.data !== undefined &&
      params.pageNumber !== undefined &&
      query.data.pageNumber !== undefined &&
      params.pageNumber !== query.data.pageNumber;

    // Handle error when retries are exhausted
    useEffect(() => {
      if (query.isError && !reportedAsPageMismatch && !errorHandledRef.current) {
        errorHandledRef.current = true;
        showErrorToast(
          "Kunne ikke hente data. Prøv venligst igen.",
          "Indhentning af data mislykkedes"
        );
      }

      // Reset error handled flag when query succeeds or query key changes
      if (query.isSuccess || query.isPending) {
        errorHandledRef.current = false;
      }
    }, [
      query.isError,
      reportedAsPageMismatch,
      query.isSuccess,
      query.isPending,
    ]);

    // Detect page mismatch: when query fails but we have data from a different page
    useEffect(() => {
      if (
        query.isError &&
        query.data && // We have data (from previous successful query)
        params.pageNumber !== undefined &&
        query.data.pageNumber !== undefined &&
        params.pageNumber !== query.data.pageNumber &&
        onPageMismatch &&
        !pageMismatchHandledRef.current
      ) {
        pageMismatchHandledRef.current = true;
        const requestedPage = params.pageNumber;
        const actualPage = query.data.pageNumber;

        // Show error toast
        showErrorToast(
          `Kunne ikke hente side ${requestedPage}. Du er blevet sendt tilbage til side ${actualPage}.`,
          "Indhentning af data mislykkedes"
        );

        // Call callback to correct the page state
        onPageMismatch(requestedPage, actualPage);
      }
    }, [query.isError, query.data, params.pageNumber, onPageMismatch]);

    return query;
  };

  const useGetById = (id: string) =>
    useQuery<Legislation>({
      queryKey: [PATH_KEYS.legislation, "byId", id],
      queryFn: async () => {
        const legislationResponse = await legislationService.getById(id);
        if (legislationResponse.success) {
          return applyConsolidationDevMetadata(legislationResponse.value);
        }
        throw restError(legislationResponse);
      },
    });

  const useGetByExternalId = (
    externalId: string | undefined,
    datasource: string
  ) => {
    // Normalize datasource to DB value before making API call
    const dbValue = getDbValue(datasource);
    
    return useQuery<Legislation>({
      queryKey: [PATH_KEYS.legislation, "byExternalId", externalId, dbValue],
      queryFn: async () => {
        if (!dbValue) {
          throw new Error(`Invalid datasource: ${datasource}`);
        }
        if (!externalId) {
          throw new Error("An external id is required");
        }
        
        const legislationResponse = await legislationService.getByExternalId(
          externalId,
          dbValue
        );
        if (legislationResponse.success) {
          return applyConsolidationDevMetadata(legislationResponse.value);
        }
        throw restError(legislationResponse);
      },
      // Don't run without a document id or with an invalid datasource
      enabled: Boolean(externalId) && !!dbValue,
    });
  };

  const useGetTimelineByExternalId = (
    externalId: string,
    options?: { enabled?: boolean }
  ) =>
    useQuery<TimelineItem[]>({
      queryKey: [PATH_KEYS.legislation, "timelineByExternalId", externalId],
      queryFn: async () => {
        const timelineResponse =
          await legislationService.getTimelineByExternalId(externalId);
        if (timelineResponse.success) {
          return normalizeTimelineDates(timelineResponse.value);
        }
        throw restError(timelineResponse);
      },
      enabled: (options?.enabled ?? true) && Boolean(externalId),
    });

  /**** Value ****/
  return {
    useGetById,
    useGetByExternalId,
    useGetPaginated,
    useGetTimelineByExternalId,
  };
};

export default useLegislation;
